import { ChangeEvent, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers'
import { isValid } from 'date-fns'
import { isEmpty } from 'lodash'

import { DefaultElementProps } from 'common/interfaces'
import { textFieldPickerStyle } from 'common/styles'
import { SizingContainer } from 'components/SizingContainer'
import { useTemplate } from 'utils/hook'
import useSetValueAsTZFormat from 'utils/useSetValueAsTZFormat'
import validationsToRules from 'utils/validationsToRules'

import { DatePicker as DatePickerProps } from './interface'

const datePickerRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/

export default function DatePicker({
  name,
  size,
  label,
  onFocus,
  onChange,
  validations,
  defaultValue,
  disableFuture,
}: DatePickerProps & DefaultElementProps): JSX.Element {
  const render = useTemplate()

  const { control, setValue, setError, clearErrors } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: validationsToRules(validations),
    defaultValue: render(defaultValue ? new Date(defaultValue).toISOString() : ''),
  })

  const onChangeDatePicker = (newValue: Date | null): void => {
    if (isEmpty(error)) {
      if (newValue) {
        const dateOnly = new Date(newValue.getFullYear(), newValue.getMonth(), newValue.getDate())
        setValue(name, dateOnly.toISOString())
      } else {
        setValue(name, newValue)
      }

      return
    }

    setValue(name, newValue)
  }

  const onChangeDatePickerField = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ): void => {
    const { value } = e.target
    if (datePickerRegex.test(value)) {
      const valueInISOString = new Date(value).toISOString()

      e.target.value = valueInISOString
      field.onChange(e)

      onChange?.({ value: valueInISOString })

      setValue(name, valueInISOString)

      clearErrors(name)

      return
    }

    if (value !== '') {
      setError(name, { message: 'Your date in wrong format' })
    } else {
      clearErrors(name)
    }
  }

  useSetValueAsTZFormat(field)

  const getDateValue = (val: unknown): Date | null => {
    if (!val) return null
    if (val instanceof Date) return isValid(val) ? val : null
    if (typeof val === 'string') {
      const date = new Date(val)
      return isValid(date) ? date : null
    }

    return null
  }

  const [open, setOpen] = useState<boolean>(false)

  const showPicker = (): void => {
    setOpen(true)
  }

  const hidePicker = (): void => {
    setOpen(false)
  }

  return (
    <SizingContainer className="date-picker-wrap" size={size} minWidth={150}>
      <MuiDatePicker
        {...field}
        value={getDateValue(field.value)}
        disableFuture={disableFuture}
        label={label}
        onChange={onChangeDatePicker}
        onOpen={showPicker}
        onClose={hidePicker}
        open={open}
        slotProps={{
          textField: (ownerState) => ({
            required: validations?.required?.value,
            sx: textFieldPickerStyle,
            fullWidth: true,
            onClick: showPicker,
            onFocus,
            onChange: onChangeDatePickerField,
            error: error !== undefined,
            helperText: error?.message,
            inputProps: {
              ...ownerState.inputProps,
              readOnly: true,
            },
          }),
        }}
      />
    </SizingContainer>
  )
}
